import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { TitleText } from "./Text";
import { Button } from "./Button";

interface Iprops {
  closeModal: () => void;
  children: React.ReactNode;
  handleSubmit?: () => void;
  title: string;
  width?: string;
  height?: string;
  noFooter?: boolean;
  noHeader?: boolean;
  handleDisable?: boolean;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
}

const MeasuresModal = ({
  closeModal,
  children,
  title,
  width = "50em",
  height = "90vh",
  noFooter,
  noHeader,
  handleSubmit,
  handleDisable,
  onFullscreenToggle,
}: Iprops) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    return () => {
      if (isFullscreen && onFullscreenToggle) {
        onFullscreenToggle(false);
      }
    };
  }, [isFullscreen, onFullscreenToggle]);

  const handleCloseModal = () => {
    if (isFullscreen && onFullscreenToggle) {
      onFullscreenToggle(false);
    }
    setIsFullscreen(false);
    closeModal();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleCloseModal();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => {
      const newFullscreenState = !prev;
      if (onFullscreenToggle) {
        onFullscreenToggle(newFullscreenState);
      }
      return newFullscreenState;
    });
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContent ref={modalRef} isFullscreen={isFullscreen} width={width} height={height}>
        {!noHeader && (
          <ModalHeader>
            <TitleText>{title}</TitleText>
            <HeaderActions>
              <FullscreenButton onClick={toggleFullscreen}>
                {isFullscreen ? "⤢" : "⤡"}
              </FullscreenButton>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </HeaderActions>
          </ModalHeader>
        )}

        <ModalBody>
          {React.cloneElement(children as React.ReactElement, { isFullscreen })}
        </ModalBody>

        {!noFooter && handleSubmit && (
          <ModalFooter>
            <ActionButton onClick={handleCloseModal}>Cancel</ActionButton>
            <SubmitButton onClick={handleSubmit} disabled={handleDisable}>
              Confirm
            </SubmitButton>
          </ModalFooter>
        )}
      </ModalContent>
    </Overlay>
  );
};

export default MeasuresModal;

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-in-out;
`;

const ModalContent = styled.div<{ isFullscreen: boolean; width: string; height: string }>`
  background-color: white;
  border-radius: 8px;
  width: ${(props) => (props.isFullscreen ? "90vw" : props.width)};
  height: ${(props) => (props.isFullscreen ? "120vh" : props.height)};
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #ddd;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #888;

  &:hover {
    color: #333;
  }
`;

const FullscreenButton = styled(CloseButton)`
  font-size: 1.3rem;
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #ddd;
  background-color: white;
  position: sticky;
  bottom: 0;
  z-index: 2;
`;

const ActionButton = styled.button`
  background: #dc3545;
  color: white;
  padding: 0.6em 1.2em;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #c82333;
  }
`;

const SubmitButton = styled.button`
  background: #007bff;
  color: white;
  padding: 0.6em 1.2em;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #0056b3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
